import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setShowResults(false);
    setSearchQuery('');
    setSearchResults(null);
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (value) => {
    setSearchQuery(value);
    clearTimeout(searchTimeout.current);
    if (value.length < 2) {
      setSearchResults(null);
      setShowResults(false);
      return;
    }
    setSearching(true);
    setShowResults(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await api.get('/search', { params: { q: value } });
        setSearchResults(res.data);
      } catch (error) {
        console.error(error);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const confirmLogout = async () => {
    setShowLogoutModal(false);
    await logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;
  const isActiveGroup = (path) => location.pathname.startsWith(path);

  const hasResults = searchResults && (
    searchResults.clients?.length > 0 ||
    searchResults.case_files?.length > 0 ||
    searchResults.budgets?.length > 0
  );
  const noResults = searchResults && !hasResults;

  // ── Nav items config ──────────────────────────────────────
  const mainNav = [
    {
      to: '/dashboard',
      label: 'Dashboard',
      active: isActive('/dashboard'),
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />,
    },
    ...(user?.role === 'notary' || user?.role === 'supervisor' ? [{
      to: '/notary-dashboard',
      label: 'Dashboard Ejecutivo',
      active: isActiveGroup('/notary-dashboard'),
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
    }] : []),
    {
      to: '/clients',
      label: 'Clientes',
      active: isActiveGroup('/clients'),
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />,
    },
    {
      to: '/case-files',
      label: 'Expedientes',
      active: isActiveGroup('/case-files'),
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />,
    },
    {
      to: '/budgets',
      label: 'Presupuestos',
      active: isActiveGroup('/budgets'),
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />,
    },
    {
      to: '/accounts-receivable',
      label: 'Cuentas por Cobrar',
      active: isActiveGroup('/accounts-receivable'),
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
    },
    {
      to: '/agenda',
      label: 'Agenda',
      active: isActiveGroup('/agenda'),
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />,
    },
    {
      to: '/manual',
      label: 'Manual de Usuario',
      active: isActive('/manual'),
      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />,
    },
  ];

  const settingsNav = user?.role === 'notary' ? [
    { to: '/settings/users',      label: 'Usuarios',          active: isActive('/settings/users'),      icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /> },
    { to: '/settings/activity-types', label: 'Tipos de Actividad', active: isActive('/settings/activity-types'), icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /> },
    { to: '/settings/procedure-types', label: 'Tipos de Trámite', active: isActive('/settings/procedure-types'), icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /> },
    { to: '/settings/system',     label: 'Config. General',   active: isActive('/settings/system'),     icon: <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></> },
    { to: '/audit-log',           label: 'Log de Auditoría',  active: isActive('/audit-log'),           icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /> },
  ] : [];

  const budgetNav = user?.role === 'notary' ? [
    { to: '/settings/notary',     label: 'Datos de la Notaría',   active: isActive('/settings/notary'),    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /> },
    { to: '/settings/budget-document-types', label: 'Tipos de Documento', active: isActiveGroup('/settings/budget-document-types'), icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /> },
  ] : [];

  const NavItem = ({ to, label, icon, active, small = false }) => (
    <Link
      to={to}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: small ? '7px 10px' : '8px 10px',
        borderRadius: '7px',
        textDecoration: 'none',
        marginBottom: '1px',
        backgroundColor: active ? 'rgba(255,255,255,0.13)' : 'transparent',
        color: active ? '#fff' : 'rgba(255,255,255,0.6)',
        fontWeight: active ? '600' : '400',
        fontSize: small ? '12.5px' : '13.5px',
        transition: 'background-color 0.15s, color 0.15s',
        gap: 9,
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = 'rgba(255,255,255,0.9)'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = active ? '#fff' : 'rgba(255,255,255,0.6)'; }}
    >
      <svg style={{ width: small ? 15 : 17, height: small ? 15 : 17, flexShrink: 0, opacity: active ? 1 : 0.75 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {icon}
      </svg>
      {label}
    </Link>
  );

  const SectionLabel = ({ label }) => (
    <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', padding: '14px 10px 5px', }}>
      {label}
    </div>
  );

  const SidebarContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <div style={{ padding: '18px 16px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="18" height="18" fill="none" stroke="#fff" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '800', color: '#fff', letterSpacing: '-0.3px', lineHeight: 1.2 }}>Lex Manager</div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: 1 }}>Sistema Notarial</div>
          </div>
        </div>
        {isMobile && (
          <button onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: 4, display: 'flex' }}>
            <svg style={{ width: 18, height: 18 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Nav */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '6px 8px' }}>
        <SectionLabel label="Principal" />
        {mainNav.map(item => <NavItem key={item.to} {...item} />)}

        {(settingsNav.length > 0 || budgetNav.length > 0) && (
          <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)', margin: '10px 4px' }} />
        )}

        {settingsNav.length > 0 && (
          <>
            <SectionLabel label="Configuración" />
            {settingsNav.map(item => <NavItem key={item.to} {...item} small />)}
          </>
        )}

        {budgetNav.length > 0 && (
          <>
            <SectionLabel label="Presupuestos" />
            {budgetNav.map(item => <NavItem key={item.to} {...item} small />)}
          </>
        )}
      </div>

      {/* User footer */}
      <div style={{ padding: '10px 8px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.06)' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: '#fff', fontSize: '13px', flexShrink: 0 }}>
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name}</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
              {user?.role === 'notary' ? 'Notario' : user?.role === 'supervisor' ? 'Supervisor' : 'Staff'}
            </div>
          </div>
          <Link to="/profile" title="Mi perfil" style={{ color: 'rgba(255,255,255,0.45)', display: 'flex', padding: 4, borderRadius: 5, transition: 'color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.color = '#fff'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}>
            <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </Link>
          <button onClick={() => setShowLogoutModal(true)} title="Cerrar sesión"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.45)', display: 'flex', padding: 4, borderRadius: 5, transition: 'color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.color = '#fff'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}>
            <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  const SIDEBAR_WIDTH = 256;

  return (
    <>
      {/* ── Header ──────────────────────────────────────────── */}
      <div style={{
        position: 'fixed', top: 0,
        left: isMobile ? 0 : SIDEBAR_WIDTH,
        right: 0, height: 58,
        backgroundColor: '#fff',
        zIndex: 1000,
        display: 'flex', alignItems: 'center',
        padding: '0 18px',
        borderBottom: '1px solid #e8eaf0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        gap: 12,
      }}>
        {/* Hamburger mobile */}
        {isMobile && (
          <button onClick={() => setSidebarOpen(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', color: '#4B5563' }}>
            <svg style={{ width: 22, height: 22 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}

        {isMobile && (
          <span style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>Lex Manager</span>
        )}

        {/* Búsqueda */}
        <div ref={searchRef} style={{ position: 'relative', flex: 1, maxWidth: isMobile ? '100%' : 380 }}>
          <div style={{ position: 'relative' }}>
            <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 15, height: 15, color: '#9CA3AF', pointerEvents: 'none' }}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Buscar clientes, expedientes..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => searchResults && setShowResults(true)}
              style={{
                width: '100%',
                padding: '7px 12px 7px 32px',
                backgroundColor: '#F4F6FB',
                border: '1.5px solid #E5E7EB',
                borderRadius: 8,
                fontSize: '13px',
                color: '#111827',
                outline: 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
              onFocusCapture={e => { e.target.style.borderColor = '#4F63D2'; e.target.style.boxShadow = '0 0 0 3px rgba(79,99,210,0.1)'; e.target.style.backgroundColor = '#fff'; }}
              onBlurCapture={e => { e.target.style.borderColor = '#E5E7EB'; e.target.style.boxShadow = 'none'; e.target.style.backgroundColor = '#F4F6FB'; }}
            />
          </div>

          {/* Resultados búsqueda */}
          {showResults && (
            <div style={{ position: 'absolute', top: 40, left: 0, right: 0, backgroundColor: '#fff', borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', zIndex: 9999, maxHeight: '70vh', overflowY: 'auto', border: '1px solid #E5E7EB' }}>
              {searching && <div style={{ textAlign: 'center', padding: '14px', fontSize: '13px', color: '#9CA3AF' }}>Buscando...</div>}
              {!searching && noResults && <div style={{ textAlign: 'center', padding: '18px', fontSize: '13px', color: '#9CA3AF' }}>Sin resultados para "{searchQuery}"</div>}
              {!searching && hasResults && (
                <>
                  {searchResults.clients?.length > 0 && (
                    <>
                      <div style={{ padding: '10px 14px 4px', fontSize: '10px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Clientes</div>
                      {searchResults.clients.map(client => (
                        <Link key={client.id} to={`/clients/${client.id}`} onClick={() => setShowResults(false)}
                          style={{ display: 'flex', alignItems: 'center', padding: '9px 14px', textDecoration: 'none', color: '#111', borderBottom: '1px solid #F9FAFB', transition: 'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#EEF0FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: '#4F63D2', flexShrink: 0, marginRight: 10, fontSize: 12 }}>
                            {client.name.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{client.name}</div>
                            <div style={{ fontSize: '12px', color: '#9CA3AF' }}>{client.email || client.phone || 'Sin contacto'}</div>
                          </div>
                        </Link>
                      ))}
                    </>
                  )}
                  {searchResults.case_files?.length > 0 && (
                    <>
                      <div style={{ padding: '10px 14px 4px', fontSize: '10px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Expedientes</div>
                      {searchResults.case_files.map(cf => (
                        <Link key={cf.id} to={`/case-files/${cf.id}`} onClick={() => setShowResults(false)}
                          style={{ display: 'flex', alignItems: 'center', padding: '9px 14px', textDecoration: 'none', color: '#111', borderBottom: '1px solid #F9FAFB', transition: 'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <div style={{ width: 30, height: 30, borderRadius: 8, background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: 10 }}>
                            <svg style={{ width: 14, height: 14, color: '#F59E0B' }} fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" /></svg>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cf.title}</div>
                            <div style={{ fontSize: '12px', color: '#9CA3AF' }}>{cf.client_name}</div>
                          </div>
                          <span style={{ fontSize: '11px', padding: '2px 7px', borderRadius: 12, background: '#FEE2E2', color: '#EF4444', fontWeight: '600', flexShrink: 0 }}>{cf.elapsed_time}</span>
                        </Link>
                      ))}
                    </>
                  )}
                  {searchResults.budgets?.length > 0 && (
                    <>
                      <div style={{ padding: '10px 14px 4px', fontSize: '10px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Presupuestos</div>
                      {searchResults.budgets.map(b => (
                        <Link key={b.id} to={`/budgets/${b.id}`} onClick={() => setShowResults(false)}
                          style={{ display: 'flex', alignItems: 'center', padding: '9px 14px', textDecoration: 'none', color: '#111', borderBottom: '1px solid #F9FAFB', transition: 'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <div style={{ width: 30, height: 30, borderRadius: 8, background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: 10 }}>
                            <svg style={{ width: 14, height: 14, color: '#10B981' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: '600', fontSize: '13px' }}>{b.folio}</div>
                            <div style={{ fontSize: '12px', color: '#9CA3AF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.client_name} · {b.document_type}</div>
                          </div>
                          <span style={{ fontSize: '11px', padding: '2px 7px', borderRadius: 12, background: '#D1FAE5', color: '#065F46', fontWeight: '600', flexShrink: 0 }}>{b.currency?.toUpperCase()}</span>
                        </Link>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Usuario en header (desktop) */}
        {!isMobile && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{user?.name}</div>
              <div style={{ fontSize: '11px', color: '#9CA3AF' }}>
                {user?.role === 'notary' ? 'Notario' : user?.role === 'supervisor' ? 'Supervisor' : 'Staff'}
              </div>
            </div>
            <Link to="/profile" title="Mi perfil"
              style={{ background: '#F4F6FB', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#4B5563', padding: '7px 9px', textDecoration: 'none', display: 'inline-flex', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#EEF0FB'}
              onMouseLeave={e => e.currentTarget.style.background = '#F4F6FB'}>
              <svg style={{ width: 16, height: 16 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </Link>
            <button onClick={() => setShowLogoutModal(true)} title="Cerrar sesión"
              style={{ background: '#F4F6FB', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#4B5563', padding: '7px 9px', display: 'flex', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#FEE2E2'}
              onMouseLeave={e => e.currentTarget.style.background = '#F4F6FB'}>
              <svg style={{ width: 16, height: 16 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* ── Sidebar desktop ──────────────────────────────────── */}
      {!isMobile && (
        <div style={{
          position: 'fixed', top: 0, left: 0,
          width: SIDEBAR_WIDTH, height: '100vh',
          backgroundColor: '#1e2a4a',
          zIndex: 999,
          boxShadow: '2px 0 12px rgba(0,0,0,0.12)',
        }}>
          <SidebarContent />
        </div>
      )}

      {/* ── Sidebar móvil ────────────────────────────────────── */}
      {isMobile && (
        <>
          {sidebarOpen && (
            <div onClick={() => setSidebarOpen(false)}
              style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.5)', zIndex: 1998, backdropFilter: 'blur(2px)' }} />
          )}
          <div style={{
            position: 'fixed', top: 0, left: 0,
            width: '78%', maxWidth: 280, height: '100vh',
            backgroundColor: '#1e2a4a',
            zIndex: 1999,
            transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
            boxShadow: '4px 0 20px rgba(0,0,0,0.25)',
          }}>
            <SidebarContent />
          </div>
        </>
      )}

      {/* ── Contenido principal ──────────────────────────────── */}
      <div style={{
        marginLeft: isMobile ? 0 : SIDEBAR_WIDTH,
        marginTop: 58,
        minHeight: 'calc(100vh - 58px)',
        backgroundColor: '#F4F6FB',
        padding: isMobile ? '14px 12px' : '22px 24px',
        overflowX: 'hidden',
      }}>
        {children}
      </div>

      {/* ── Modal logout ─────────────────────────────────────── */}
      {showLogoutModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: '32px 28px', maxWidth: 360, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', textAlign: 'center' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', backgroundColor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg style={{ width: 24, height: 24, color: '#EF4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </div>
            <h5 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: '#111827' }}>Cerrar sesión</h5>
            <p style={{ color: '#6B7280', fontSize: 13, margin: '0 0 24px' }}>¿Estás seguro que deseas cerrar tu sesión?</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => setShowLogoutModal(false)}
                style={{ padding: '8px 22px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                Cancelar
              </button>
              <button onClick={confirmLogout}
                style={{ padding: '8px 22px', borderRadius: 8, border: 'none', backgroundColor: '#EF4444', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#DC2626'}
                onMouseLeave={e => e.currentTarget.style.background = '#EF4444'}>
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        body { background-color: #F4F6FB !important; }
        * { box-sizing: border-box; }
        @media (max-width: 767px) {
          .container-fluid { padding-left: 0 !important; padding-right: 0 !important; }
        }
      `}</style>
    </>
  );
};

export default Layout;
